import { TokenHelper } from 'common/helpers/auth/token-helper';
import {
    MainLayout,
    MasterLayout,
    ToolbarLayout,
    ToolbarLayoutLeft,
    ToolbarLayoutRight,
} from 'common/imports/content-layout';
import i18next from 'i18next';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import en from '../../user-management/i18n/en-us.json';
import pt from '../../user-management/i18n/pt-br.json';
import { UserManagementModel as Model } from '../../user-management/model/user-management.model';
import { UserManagementService as Service } from '../../user-management/services/user-management.service';
import { ChangePassword } from '../../user-management/components/child/ChangePassword';
import { DepartmentSelectorService } from 'app/department-selector/services/department-selector.service';
import DataTable, { TableColumn } from 'react-data-table-component';
import { itLabTableStyle } from 'common/helpers/table/table-style';
import { tableHelper } from 'common/helpers/table/table-helper';
import { NotificationCenter } from 'app/notification-center/NotificationCenter';
import { useSearchParams } from 'react-router-dom';

interface DepartmentData {
    id: number;
    name: string;
}

enum Tabs {
    Info = 1,
    NotificationCenter = 2
}

export function MyProfile() {
    i18next.addResourceBundle('us', 'translation', en);
    i18next.addResourceBundle('br', 'translation', pt);

    const { t } = useTranslation();
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
    const [departments, setDepartments] = useState<DepartmentData[]>([]);
    const [loadingDepartments, setLoadingDepartments] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = searchParams.get('tab') === 'notificationCenter' ? Tabs.NotificationCenter : Tabs.Info;

    const tokenHelper = new TokenHelper();
    const userName = tokenHelper.GetUserName();
    const userId = tokenHelper.GetId();

    const noDataComponent = tableHelper.NoDataComponent();

    let initialData: Model = {
        id: userId ?? '0',
        name: '',
        email: '',
        userName: '',
        isActive: true,
        authenticationType: undefined,
    };

    const [data, setData] = useState<Model>(initialData);

    const handleTabChange = (newTab: Tabs) => {
        const tab = newTab === Tabs.NotificationCenter ? 'notificationCenter' : 'basicInfo';
        setSearchParams({ tab });
    };

    const handleChangePassword = () => {
        setShowChangePasswordModal(true);
    }

    const handleClosePasswordModal = () => {
        setShowChangePasswordModal(false);
    }

    const fetchUserData = async () => {
        try {
            if (userId) {
                const response = await Service.getUserById(userId.toString());
                if (response && !response.hasErrors) {
                    setData(response.data);
                }
            }
        } catch (err) {
            toast.error(`${t('crud.read.errorMessage')}`);
        }
    };

    const fetchUserDepartments = async () => {
        try {
            setLoadingDepartments(true);
            const response = await DepartmentSelectorService.getUserAndDepartments(userId);
            if (response && !response.hasErrors) {
                setDepartments(response.data.departments || []);
            }
        } catch (err) {
            toast.error(`${t('crud.read.errorMessage')}`);
        } finally {
            setLoadingDepartments(false);
        }
    }

    const departmentColumns: TableColumn<DepartmentData>[] = [
        {
            name: `${t('table.id')}`,
            selector: (row) => row.id.toString(),
            width: '80px',
            center: true
        },
        {
            name: `${t('table.name')}`,
            selector: (row) => row.name,
            grow: 2
        }
    ];

    useEffect(() => {
        fetchUserData();
        if (userId) {
            fetchUserDepartments();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <MasterLayout>
            <ToolbarLayout>
                <ToolbarLayoutLeft />
                <ToolbarLayoutRight />
            </ToolbarLayout>
            <MainLayout>
                <div className="card">
                    <div className="card-body d-flex justify-content-between pb-0">
                        <ul className="nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-5 fw-bolder flex-nowrap">
                            <li className="nav-item cursor-pointer">
                                <div
                                    className={`nav-link text-active-primary ${activeTab === Tabs.Info ? 'active' : ''}`}
                                    onClick={() => handleTabChange(Tabs.Info)}
                                >
                                    {t('basicInfoTab')}
                                </div>
                            </li>
                            <li className="nav-item cursor-pointer">
                                <div
                                    className={`nav-link text-active-primary ${activeTab === Tabs.NotificationCenter ? 'active' : ''}`}
                                    onClick={() => handleTabChange(Tabs.NotificationCenter)}
                                >
                                    {t('notificationCenterTab')}
                                </div>
                            </li>
                        </ul>
                        {activeTab === Tabs.Info && data.authenticationType === 1 && !data.isClientAPI && (
                            <>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-primary fw-bolder"
                                    onClick={() => handleChangePassword()}
                                    disabled={!data.isActive}
                                >
                                    {t('form.passwordComponent.title')}
                                </button>
                            </>
                        )}
                    </div>
                    {activeTab === Tabs.Info && (
                        <div className="card-body pt-0">
                            <div className="row">
                                <div className="col-12 mb-10">
                                    <label className="form-label mt-10">{t('form.name')}</label>
                                    <input
                                        type="text"
                                        name="name"
                                        className="form-control form-control-lg form-control-solid"
                                        value={data.name ?? ''}
                                        readOnly
                                        disabled
                                    />
                                </div>
                                <div className="col-md-6 mb-10">
                                    <label className="form-label">{t('form.email')}</label>
                                    <input
                                        type="text"
                                        name="email"
                                        className="form-control form-control-lg form-control-solid"
                                        value={data.email ?? ''}
                                        readOnly
                                        disabled
                                    />
                                </div>
                                <div className="col-md-6 mb-10">
                                    <label className="form-label">{t('form.username')}</label>
                                    <input
                                        type="text"
                                        name="userName"
                                        className="form-control form-control-lg form-control-solid"
                                        value={data.userName ?? ''}
                                        readOnly
                                        disabled
                                    />
                                </div>
                            </div>
                            <div className="row mt-10">
                                <div className="col-12">
                                    <label className="form-label mb-4">{t('table.departments')}</label>
                                    <div className="card card-flush">
                                        <div className="card-body p-0">
                                            <DataTable
                                                columns={departmentColumns}
                                                data={departments}
                                                noDataComponent={noDataComponent}
                                                dense
                                                highlightOnHover
                                                progressPending={loadingDepartments}
                                                progressComponent={tableHelper.Loader()}
                                                customStyles={itLabTableStyle}
                                                persistTableHead
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                    )}
                    {activeTab === Tabs.NotificationCenter && <NotificationCenter userId={userId} />}
                </div>
            </MainLayout>
            <ChangePassword
                open={showChangePasswordModal}
                handleClose={handleClosePasswordModal}
                userName={userName}
                name={userName}
                id={userId}
            />
        </MasterLayout>
    );
}
